/**
 * Portal-specific component showing Missive attachment capabilities.
 * Styled for parchment theme.
 */
export function PortalMissiveInfo({ level, stars }: { level: number; stars: number }) {
    // Calculate current capabilities
    const canSendResources = level >= 1;
    const canSendBlueprints = level >= 3;
    const canSendItems = level >= 5;

    // Mystical costs based on stars
    const RESOURCE_COST_PER_50: Record<number, number> = { 1: 10, 2: 8, 3: 6, 4: 4, 5: 2 };
    const ITEM_COST: Record<number, number> = { 1: 80, 2: 80, 3: 60, 4: 40, 5: 20 };

    const currentResourceCost = RESOURCE_COST_PER_50[stars] || 10;
    const currentItemCost = ITEM_COST[stars] || 80;

    return (
        <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">🌀</span>
                <h2 className="text-xl font-serif text-amber-900">Missive Attachments</h2>
            </div>

            <p className="text-amber-800 font-serif text-sm mb-4 leading-relaxed">
                The Portal allows you to send resources, blueprints, and items through Missives to other players.
                Higher levels unlock new attachment types, while higher star ratings reduce the Mystical cost.
            </p>

            {/* Current Capabilities */}
            <div className="bg-amber-100/40 rounded-lg p-4 mb-4 border border-amber-400/30">
                <h3 className="text-sm font-serif font-semibold text-purple-800 uppercase tracking-wider mb-3">
                    Your Portal Capabilities
                </h3>
                <div className="flex flex-wrap gap-4 font-serif">
                    <div className={`flex items-center gap-2 ${canSendResources ? 'text-green-700' : 'text-amber-600/60'}`}>
                        <span>{canSendResources ? '✓' : '✗'}</span>
                        <span>Resources</span>
                        {canSendResources && (
                            <span className="text-xs text-purple-700">({currentResourceCost} ✨ per 50)</span>
                        )}
                    </div>
                    <div className={`flex items-center gap-2 ${canSendBlueprints ? 'text-green-700' : 'text-amber-600/60'}`}>
                        <span>{canSendBlueprints ? '✓' : '✗'}</span>
                        <span>Blueprints</span>
                        {canSendBlueprints && (
                            <span className="text-xs text-purple-700">({currentItemCost} ✨ each)</span>
                        )}
                    </div>
                    <div className={`flex items-center gap-2 ${canSendItems ? 'text-green-700' : 'text-amber-600/60'}`}>
                        <span>{canSendItems ? '✓' : '✗'}</span>
                        <span>Items</span>
                        {canSendItems && (
                            <span className="text-xs text-purple-700">({currentItemCost} ✨ each)</span>
                        )}
                    </div>
                </div>
            </div>

            {/* Unlock Requirements Table */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">Unlock Requirements</h3>
                <p className="text-xs font-serif text-amber-700 mb-2">
                    Higher Portal levels unlock additional attachment types for Missives.
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-serif">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-800 font-medium">Portal Level</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Resources</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Blueprints</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Items</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                { lvl: 'None/0', resources: false, blueprints: false, items: false },
                                { lvl: '1-2', resources: true, blueprints: false, items: false },
                                { lvl: '3-4', resources: true, blueprints: true, items: false },
                                { lvl: '5', resources: true, blueprints: true, items: true },
                            ].map((row, idx) => (
                                <tr
                                    key={row.lvl}
                                    className={`border-b border-amber-300/30 ${
                                        (idx === 1 && level >= 1 && level <= 2) ||
                                        (idx === 2 && level >= 3 && level <= 4) ||
                                        (idx === 3 && level >= 5)
                                            ? 'bg-purple-200/30'
                                            : ''
                                    }`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">{row.lvl}</td>
                                    <td className="py-2 px-3 text-center">
                                        <span className={row.resources ? 'text-green-700' : 'text-red-700'}>
                                            {row.resources ? '✓' : '✗'}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className={row.blueprints ? 'text-green-700' : 'text-red-700'}>
                                            {row.blueprints ? '✓' : '✗'}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className={row.items ? 'text-green-700' : 'text-red-700'}>
                                            {row.items ? '✓' : '✗'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Mystical Cost Table */}
            <div>
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">Mystical Costs</h3>
                <p className="text-xs font-serif text-amber-700 mb-2">
                    Higher star ratings reduce the Mystical resource cost to send attachments.
                    Resource cost is calculated per 50 total resources combined across all types.
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-serif">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-800 font-medium">Portal Stars</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Per 50 Resources</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Per Blueprint/Item</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((s) => (
                                <tr
                                    key={s}
                                    className={`border-b border-amber-300/30 ${stars === s ? 'bg-purple-200/30' : ''}`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">
                                        {'★'.repeat(s)}{'☆'.repeat(5 - s)}
                                    </td>
                                    <td className="py-2 px-3 text-center text-purple-800">
                                        {RESOURCE_COST_PER_50[s]} ✨
                                    </td>
                                    <td className="py-2 px-3 text-center text-purple-800">
                                        {ITEM_COST[s]} ✨
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Example Calculation */}
            <div className="mt-4 p-3 bg-amber-100/40 rounded-lg border border-amber-400/30">
                <h4 className="text-xs font-serif font-semibold text-amber-800 uppercase mb-2">Example Cost Calculation</h4>
                <p className="text-xs font-serif text-amber-700">
                    Sending 100 Tea + 50 Wood (150 total resources) + 2 Items with a {stars}-star Portal:
                </p>
                <p className="text-sm font-serif text-purple-800 mt-1">
                    Resources: ceil(150 / 50) × {currentResourceCost} = <span className="text-amber-900 font-medium">{Math.ceil(150 / 50) * currentResourceCost} ✨</span>
                    <br />
                    Items: 2 × {currentItemCost} = <span className="text-amber-900 font-medium">{2 * currentItemCost} ✨</span>
                    <br />
                    <span className="text-green-700 font-medium">Total: {Math.ceil(150 / 50) * currentResourceCost + 2 * currentItemCost} ✨</span>
                </p>
            </div>
        </div>
    );
}
